import { useEffect, useState } from "react";
import {
  getLifeStages,
  getVisibleQuestions,
  formatStageLabel,
  getEntryReasons,
  getLifeStageDetail,
  getLifeStageMeta,
} from "../components/Reusablesteps/Resolvelifestage";
import { useNavigate } from "react-router-dom";
import StartCareJourneyScreen from "../components/CareJourney/Startcarejourney";
import NewPatientScreen from "../components/CareJourney/Newpatientscreen";
import ProfileScreen from "../components/CareJourney/Profilescreen";
import DigitalHealthFAB from "../components/Digitalhealth";
import CareRecommendationScreen from "../components/CareJourney/CareRecommendationScreen";
import HeightWeightScreen from "../components/CareJourney/Heightweightscreen";
import BasicLifeStageScreen from "../components/CareJourney/Basiclifestagescreen";
import StepperProgress from "../components/Reusablesteps/Stepperprogress";
import styles from '../styles/pagestyles.module.css';
import { BtnAskNavigator, BtnBack, BtnContinue } from "../components/Reusablecomponents/Button/Reusableutton";
import useScrollToBottom from "../hooks/useScrollToBottom";
import { ScrollToBottomButton } from "../components/common/ScrollToBottomButton";
import ConsentScreen from "../components/Reusablecomponents/Consentscreen/Consentscreen";
import { ScreenHeaderDFO } from "../components/HealthAssessment/SharedUI";
// import careJourneyContent from '../components/CareJourney/CareJourney.json';
import { BASE_URL } from "../api/apiClient";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  purple900: "#1A0A3C",
  purple800: "#2D1466",
  purple700: "#3B1A8C",
  purple600: "#4C24A8",
  purple500: "#5B2EBF",
  purpleBtn: "#3D1A8A",
  purpleLight: "#EDE9FB",
  purpleBorder: "#6B48D3",
  purpleTag: "#E8E3FA",
  dark: "#0F0A1E",
  heading: "#1A1033",
  body: "#374151",
  sub: "#6B7280",
  muted: "#9CA3AF",
  border: "#E5E7EB",
  borderFocus: "#6B48D3",
  pageBg: "#FFFFFF",
  lavenderBg: "#F4F1FD",
  cardBg: "#FFFFFF",
  inputBg: "#FFFFFF",
  trustBg: "#EDE9FB",
  infoBg: "#EFF6FF",
  infoBorder: "#BFDBFE",
  infoText: "#1E40AF",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  pink: "#EC4899",
  pinkLight: "#FFF0F5",
  pinkBorder: "#FBCFE8",
};

// ─── getStageKey ───────────────────────────────────────────────────────────
export function getStageKey(stage) {
  if (!stage) return "youngAdult";
  const s = stage.toString().toLowerCase().replace(/\s+/g, "");
  if (s.includes("adolesc")) return "adolescence";
  if (s.includes("young")) return "youngAdult";
  if (s.includes("mature")) return "matureReproductive";
  if (s.includes("peri")) return "perimenopause";
  if (s.includes("postmeno")) return "postmenopause";
  if (s.includes("meno")) return "menopause";
  if (s.includes("geriatric")) return "geriatric";
  return stage;
}


// ─── ENTRY_REASONS_STATIC ──────────────────────────────────────────────────
export const ENTRY_REASONS_STATIC = {
  menstrual_health: { label: "Menstrual Health", emoji: "🗓️", desc: "Cycle irregularities, cramps, heavy or missed periods." },
  pcos_screening: { label: "PCOS Screening", emoji: "🔬", desc: "Hormonal symptoms, acne, hair growth, weight changes." },
  pelvic_pain: { label: "Pelvic Pain", emoji: "💜", desc: "Lower abdominal, back, or pelvic floor pain." },
  menopause_symptoms: { label: "Menopause Symptoms", emoji: "🌡️", desc: "Hot flushes, night sweats, mood and sleep changes." },
  infection_gynae: { label: "Infection / Discharge", emoji: "🔬", desc: "Unusual discharge, itching, burning, or odour." },
  early_pregnancy: { label: "Early Pregnancy", emoji: "🤰", desc: "Missed period, nausea, breast tenderness, fatigue." },
  preconception: { label: "Preconception Care", emoji: "🌿", desc: "Cycle tracking, supplements, health checks before TTC." },
  fertility: { label: "Fertility", emoji: "🌿", desc: "Challenges conceiving, ovulation, or cycle concerns." },
  heavy_bleeding: { label: "Heavy Bleeding", emoji: "🩸", desc: "Soaking pads, clots, prolonged or irregular bleeding." },
  urogenital: { label: "Urogenital Health", emoji: "💧", desc: "Dryness, urgency, leakage, or pelvic floor concerns." },
  bone_health: { label: "Bone & Heart Health", emoji: "🦴", desc: "Joint pain, fracture risk, blood pressure, cholesterol." },
  mental_wellbeing: { label: "Mood & Mental Wellbeing", emoji: "🧠", desc: "Low mood, anxiety, sleep problems, or burnout." },
  contraception: { label: "Contraception", emoji: "💊", desc: "Choosing, changing, or managing contraception." },
  puberty_education: { label: "Puberty & Education", emoji: "📚", desc: "First period, cycle questions, body changes guidance." },
  lifestyle: { label: "Lifestyle & Wellness", emoji: "🥗", desc: "Weight, energy, sleep, stress, nutrition, fitness." },
  preventive_care: { label: "Preventive Care", emoji: "✅", desc: "Screenings, vaccinations, wellness checks." },
  chronic_disease: { label: "Chronic Condition Care", emoji: "🩺", desc: "Diabetes, thyroid, BP, ongoing medication management." },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPER — resolve life stage info from profile (used across all screens)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * resolveLifeStageFromProfile(profile, lifeStageAnswers)
 * Single source of truth for stage resolution used by every screen.
 * Priority: lifeStageAnswers.stages → getLifeStages(profile.age, profile.marital)
 */


// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE PRIMITIVE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
export const IconCircle = ({ children, size = 36, bg = T.purpleLight }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", background: bg,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, fontSize: size * 0.42,
  }}>
    {children}
  </div>
);

export const StageBadge = ({ label, emoji }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    background: T.purpleTag, borderRadius: 20, padding: "4px 12px",
    fontSize: 11, fontWeight: 600, color: T.purple700,
  }}>
    {emoji || "🌸"} {label}
  </span>
);

export const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.08em", textTransform: "uppercase", padding: "10px 0 4px", margin: 0 }}>
    {children}
  </p>
);

export const Chip = ({ label, selected, onClick }) => (
  <button onClick={onClick} style={{
    padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: selected ? 600 : 400,
    border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: selected ? T.purpleLight : "#fff",
    color: selected ? T.purple700 : T.sub,
    cursor: "pointer", transition: "all 0.15s",
  }}>
    {label}
  </button>
);

export const SymptomChip = ({ label, selected, onClick }) => {
  const icon = SYMPTOM_ICONS[label] || "•";
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
      borderRadius: 12, textAlign: "left", position: "relative",
      border: selected ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
      // background: selected ? T.purpleLight : "#fff",
      color: selected ? T.purple700 : T.body,
      cursor: "pointer", fontSize: 12, fontWeight: selected ? 600 : 400,
      transition: "all 0.18s",
    }}>
      <IconCircle size={32} bg={selected ? "#D8D0F8" : "#F3F4F6"}>{icon}</IconCircle>
      <span style={{ flex: 1 }}>{label}</span>
      {selected && (
        <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.purple700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
            <path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </button>
  );
};

export const CareCheckbox = ({ checked, onChange }) => (
  <div onClick={onChange} style={{
    width: 20, height: 20, borderRadius: 5, flexShrink: 0, cursor: "pointer",
    border: checked ? "none" : `2px solid ${T.border}`,
    background: checked ? T.purple700 : "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    {checked && <svg width="10" height="8" viewBox="0 0 12 9" fill="none"><path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
  </div>
);

export const Toggle = ({ on, onChange }) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: "pointer",
    background: on ? T.purple700 : T.border, position: "relative", transition: "background 0.2s",
  }}>
    <div style={{
      width: 16, height: 16, borderRadius: "50%", background: "#fff",
      position: "absolute", top: 3, left: on ? 21 : 3, transition: "left 0.2s",
    }} />
  </div>
);

export const InfoBox = ({ children }) => (
  <div style={{ background: T.infoBg, border: `1px solid ${T.infoBorder}`, borderRadius: 8, padding: "8px 10px" }}>
    <p style={{ fontSize: 10, color: T.infoText, margin: 0, lineHeight: 1.5 }}>ℹ️ {children}</p>
  </div>
);

export const PurpleInfoBox = ({ icon, title, desc }) => (
  <div style={{ background: T.lavenderBg, borderRadius: 8, padding: "8px 10px", display: "flex", gap: 8 }}>
    <span style={{ fontSize: 14 }}>{icon}</span>
    <div>
      <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, margin: "0 0 2px" }}>{title}</p>
      <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{desc}</p>
    </div>
  </div>
);

export const TrustBar = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 12, padding: "8px 0 2px" }}>
    {["🔒 Secure", "👩‍⚕️ Expert-led", "🌸 Personalised"].map(item => (
      <span key={item} style={{ fontSize: 10, color: T.muted, fontWeight: 500 }}>{item}</span>
    ))}
  </div>
);

export const FeatureItem = ({ icon, title, desc }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
    <div style={{ width: 28, height: 28, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{icon}</div>
    <div>
      <p style={{ fontWeight: 600, fontSize: 11, color: T.heading, margin: "0 0 2px" }}>{title}</p>
      <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{desc}</p>
    </div>
  </div>
);

export const StyledSelect = ({ value, onChange, options, placeholder }) => (
  <div style={{ position: "relative" }}>
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      width: "100%", padding: "10px 36px 10px 12px", borderRadius: 8, appearance: "none",
      border: `1.5px solid ${value ? T.purpleBorder : T.border}`,
      fontSize: 12, color: value ? T.heading : T.muted,
      background: "#fff", cursor: "pointer", outline: "none",
    }}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.muted, fontSize: 12 }}>▾</span>
  </div>
);

export const YesNoBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: "6px 16px", borderRadius: 8, fontSize: 11, fontWeight: active ? 700 : 400,
    border: active ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
    background: active ? T.purpleLight : "#fff",
    color: active ? T.purple700 : T.sub, cursor: "pointer", transition: "all 0.15s",
  }}>
    {label}
  </button>
);

export const YesNo = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 8 }}>
    {["Yes", "No"].map(opt => (
      <button key={opt} onClick={() => onChange(opt)} style={{
        flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 12,
        border: value === opt ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
        background: value === opt ? T.purpleLight : "#fff",
        color: value === opt ? T.purple700 : T.sub,
        fontWeight: value === opt ? 700 : 400, cursor: "pointer", transition: "all 0.15s",
      }}>
        {opt}
      </button>
    ))}
  </div>
);



export const FooterNote = ({ children }) => (
  <p style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.muted, margin: 0, whiteSpace: "nowrap" }}>
    🔒 {children}
  </p>
);

export const FooterBar = ({ left, center, right }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 0 0", marginTop: 10, borderTop: `1px solid ${T.border}`,
  }}>
    <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>{left}</div>
    <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>{center}</div>
    <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>{right}</div>
  </div>
);

export const PageWrap = ({ children }) => (
  <div style={{
    minHeight: "100vh",
    background: "linear-gradient(135deg,#e8f4f8 0%,#f0eeff 50%,#e8f4f8 100%)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "48px 16px 24px", fontFamily: "system-ui, -apple-system, sans-serif",
    boxSizing: "border-box",
  }}>
    <div style={{
      width: "100%", maxWidth: 960, background: "white", borderRadius: 20,
      padding: "24px 32px 20px", boxShadow: "0 4px 40px rgba(61,26,138,0.09)",
      border: `1px solid ${T.border}`, boxSizing: "border-box",
    }}>
      {children}
    </div>
  </div>
);

export const RightCard = ({ children, style = {} }) => (
  <div style={{
    background: "#fff", borderRadius: 14, padding: 14, border: `1px solid ${T.border}`,
    display: "flex", flexDirection: "column", gap: 10, ...style,
  }}>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SYMPTOM ICONS MAP
// ─────────────────────────────────────────────────────────────────────────────
const SYMPTOM_ICONS = {
  "Irregular periods": "🗓️", "Acne": "🫧", "Facial hair growth": "✨",
  "Weight gain": "⚖️", "Hair thinning": "💆", "Pelvic pain": "💜",
  "Bloating": "🫁", "Breast tenderness": "🌸", "Mood swings": "🧠",
  "Headaches/migraines": "🤕", "Nausea": "🤢", "Fatigue": "😴",
  "None of the above": "✅", "Lower abdomen": "📍", "Lower back": "🔙",
  "Pelvic floor / between the hip bones": "💜", "During sex (deep pain)": "❤️",
  "During urination": "💧", "During bowel movements": "⚠️",
  "Hot flushes": "🌡️", "Night sweats": "💧", "Sleep disturbances": "🌙",
  "Reduced sex drive": "❤️", "Vaginal dryness": "💧",
  "Joint pain or stiffness": "🦴",
  "Unusual discharge (colour, smell, or texture)": "🔬",
  "Itching or burning": "🔥", "Pain during sex": "❤️",
  "Pain or burning when urinating": "💧", "Fever or chills": "🌡️",
  "Pelvic pain or pressure": "💜",
};
// const getSymptomIcon = (label) => SYMPTOM_ICONS[label] || "•";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

export function getSafetySymptoms(routeKey,careJourneyContent) {
  const config = careJourneyContent.screens?.safetyCheck || {};
  return config[routeKey] || config.default || [];
}
const OptionCard = ({ emoji, label, desc, selected, onClick, badge }) => (
  <button onClick={onClick} style={{
    position: "relative", textAlign: "left", borderRadius: 12, padding: "12px 12px 10px",
    // background: selected ? T.purpleLight : T.cardBg,
    border: selected ? `2px solid ${T.purpleBorder}` : `1px solid ${T.border}`,
    cursor: "pointer", display: "flex", flexDirection: "column", transition: "all 0.2s",
  }}>
    {selected && (
      <div style={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, background: T.purple700, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="10" height="8" viewBox="0 0 12 9" fill="none"><path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
    )}
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <IconCircle size={36}>{emoji}</IconCircle>
      <p style={{ fontWeight: 700, fontSize: 11, lineHeight: 1.3, color: T.heading, paddingRight: selected ? 20 : 0, margin: 0 }}>{label}</p>
    </div>
    <p style={{ fontSize: 10, lineHeight: 1.4, color: T.sub, flex: 1, margin: "0 0 6px" }}>{desc}</p>
    {badge && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, color: badge.color, background: badge.bg, alignSelf: "flex-start", marginBottom: 4 }}>{badge.label}</span>}
    {/* <span style={{ fontSize: 11, fontWeight: 600, color: T.purple700 }}>Explore →</span> */}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// ADAPTIVE QUESTIONS BUILDER
// ─────────────────────────────────────────────────────────────────────────────

function matchesClause(clause, ctx) {
  if (clause.always) return true;
  if (clause.stage) return ctx.hasStage(clause.stage);
  if (clause.route) return ctx.route === clause.route;
  if (clause.symptom) return ctx.hasSym(clause.symptom);
  if (clause.journey) return ctx.selectedJourney === clause.journey;
  if (clause.tryingToConceive) return ctx.tryingToConceive === clause.tryingToConceive;
  if (clause.severity) return ctx.severity === clause.severity;
  if (clause.duration) return ctx.duration === clause.duration;
  if (clause.urgentPresent) return ctx.urgentSymptoms.length > 0 && !ctx.urgentSymptoms.includes("None of these");
  return false;
}

function buildAdaptiveQuestions(allData,careJourneyContent) {
  const {
    selectedJourney,
    selectedRoute,
    selectedSymptoms = [],
    duration,
    severity,
    tryingToConceive,
    urgentSymptoms = [],
    lifeStageAnswers = {},
    profile = {},
  } = allData;

  const stages = lifeStageAnswers?.stages?.length > 0
    ? lifeStageAnswers.stages
    : getLifeStages(profile?.age, profile?.marital);

  const ctx = {
    hasStage: key => stages.includes(key),
    hasSym: s => selectedSymptoms.includes(s),
    route: selectedRoute || "",
    selectedJourney,
    tryingToConceive,
    severity,
    duration,
    urgentSymptoms,
  };

  const pool = careJourneyContent.screens?.adaptiveQuestions?.questions || [];
  const limit = careJourneyContent.screens?.adaptiveQuestions?.poolLimit || 8;

  const qs = pool.filter(q => (q.when || []).some(clause => matchesClause(clause, ctx)));

  const seen = new Set();
  return qs
    .filter(q => {
      if (seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    })
    .slice(0, limit);
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 7 — Entry Reason
// ─────────────────────────────────────────────────────────────────────────────

// ─── Resolve entry reasons purely from careJourneyContent ──────────────────
function resolveEntryReasonsFromJSON(stages,careJourneyContent) {
  const config = careJourneyContent.screens?.entryReasons || {};
  for (const s of stages) {
    if (config[s]) {
      const cfg = config[s];
      return {
        journeyTitle: cfg.journeyTitle,
        screenTitle: cfg.screenTitle,
        options: (cfg.options || [])
          .filter((o) => o.status !== 0)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      };
    }
  }
  return null;
}

function resolveLifeStageFromProfile(profile = {}, lifeStageAnswers = {},careJourneyContent) {
  const stages =
    lifeStageAnswers?.stages?.length > 0
      ? lifeStageAnswers.stages
      : getLifeStages(profile?.age, profile?.marital);

  const primaryStage = stages[0] || "youngAdult";
  const stageDetail = getLifeStageDetail(primaryStage) || {};
  const stageMeta = getLifeStageMeta(primaryStage) || { emoji: "🌸", color: "#F4F1FD" };
  const stageName = stageDetail.name || formatStageLabel(primaryStage);
  const journey = resolveEntryReasonsFromJSON(stages,careJourneyContent);
  const journeyTitle = journey?.journeyTitle || "Women's Health Journey";
  const journeyEmoji = stageMeta.emoji || "🌸";

  return { stages, primaryStage, stageDetail, stageMeta, stageName, journey, journeyTitle, journeyEmoji };
}

export function EntryReasonScreen({
  profile,
  onNext,
  onBack,
  initialSelected = [],
  stepIndex,
  totalSteps,
  lifeStageAnswers = {},
  careJourneyContent
}) {
  const [selected, setSelected] = useState(initialSelected);

  const { stageMeta, stageName, journey } = resolveLifeStageFromProfile(profile, lifeStageAnswers,careJourneyContent);
  const toggle = (val) => setSelected((p) => (p.includes(val) ? p.filter((v) => v !== val) : [...p, val]));

  const getUrgencyBadge = (routeKey) => {
    if (!routeKey) return null;
    if (routeKey.toLowerCase().includes("urgent") || routeKey.toLowerCase().includes("triage"))
      return { label: "Urgent", color: T.red, bg: "#FEF2F2" };
    return null;
  };

  // selectedRoute now resolves to the machine-readable routeKey, which
  // ConditionSpecificIntakeScreen looks up directly in conditionIntake.
  const selectedOption = journey?.options?.find((o) => selected.includes(o.value));
  const selectedRoute = selectedOption?.routeKey || null;

  if (!journey) {
    // No JSON config for this stage yet — fail visibly instead of silently
    // rendering an empty grid, so gaps in care-journey.json surface immediately.
    return (
      <div style={{ padding: 24, color: T.red, fontSize: 13 }}>
        No entry-reason content configured for this life stage yet.
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ maxWidth: 480 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 4 }}>
            {journey.screenTitle || "What brings you in today?"}
          </h1>
          <p style={{ fontSize: 11, color: T.sub, marginBottom: 8 }}>
            Based on your life stage, choose the option that best matches your concern.
          </p>
          <StageBadge label={journey.journeyTitle || stageName} emoji={stageMeta.emoji} />
        </div>
        <StepperProgress current={stepIndex} total={totalSteps} />
      </div>

      <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1px solid ${T.border}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
          {journey.options.map((opt) => (
            <OptionCard
              key={opt.id || opt.value}
              emoji={opt.emoji}
              label={opt.label}
              desc={opt.desc}
              selected={selected.includes(opt.value)}
              onClick={() => toggle(opt.value)}
              badge={getUrgencyBadge(opt.routeKey)}
            />
          ))}
        </div>
      </div>

      <FooterBar
        left={<BtnBack variant="outline" onClick={onBack} />}
        center={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <BtnAskNavigator onClick={() => { }} />
            <FooterNote>You can change your response later.</FooterNote>
          </div>
        }
        right={
          <BtnContinue
            variant="primary"
            label="Continue"
            disabled={selected.length === 0}
            onClick={() => onNext({ selectedReasons: selected, selectedRoute })}
          />
        }
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 8 — Condition Specific Intake
// ─────────────────────────────────────────────────────────────────────────────

export function ConditionSpecificIntakeScreen({
  onNext,
  onBack,
  stepIndex,
  totalSteps,
  selectedRoute,
  selectedJourney,
  profile,
  lifeStageAnswers = {},
  initialValues = {},
   careJourneyContent
}) {
  const [selectedSymptoms, setSelectedSymptoms] = useState(initialValues.selectedSymptoms || []);
  const [duration, setDuration] = useState(initialValues.duration || "");
  const [severity, setSeverity] = useState(initialValues.severity || "");
  const [tryingToConceive, setTryingToConceive] = useState(initialValues.tryingToConceive || "");

  const { scrollRef, showButton, scrollToBottom } = useScrollToBottom();

  const { stageMeta, stageName, journeyTitle, journeyEmoji } = resolveLifeStageFromProfile(profile, lifeStageAnswers,careJourneyContent);

  const conditionConfig = careJourneyContent.screens?.conditionIntake || {};
  const sharedConfig = careJourneyContent.screens?.conditionIntakeShared || {};

  const routeKey = selectedRoute || "pcos_screening";
  const meta = conditionConfig[routeKey] || {
    title: "Tell us a bit more about your symptoms",
    desc: "We'll use your answers to personalise your next steps.",
    symptoms: [],
    showTryingConceive: false,
  };

  const symptoms = meta.symptoms?.length ? meta.symptoms : ["No symptoms configured for this route"];
  const DURATION_OPTS = sharedConfig.durationOptions || ["Less than 1 month", "1–3 months", "3–6 months", "6 months to 1 year", "More than 1 year"];
  const SEVERITY_OPTS = sharedConfig.severityOptions || ["Mild", "Moderate", "Severe", "Very severe"];
  const showTryingConceive = !!meta.showTryingConceive;

  const toggleSymptom = (s) =>
    setSelectedSymptoms((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <>
      <div ref={scrollRef} style={{ height: "84vh", overflow: "scroll" }}>
        <div
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: stageMeta.color || T.purpleTag, borderRadius: 20, padding: "4px 12px",
            marginBottom: 14, fontSize: 11, fontWeight: 600, color: T.purple700,
          }}
        >
          {stageMeta.emoji} {stageName}
        </div>

        <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ maxWidth: 560 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, marginBottom: 6, lineHeight: 1.2 }}>
              {meta.title}
            </h1>
            <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>{meta.desc}</p>
          </div>
          <StepperProgress current={stepIndex} total={totalSteps} />
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ background: "#fff", borderRadius: 14, padding: 18, border: `1px solid ${T.border}`, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, margin: 0, marginBottom: 2 }}>
                    What symptoms are you experiencing?
                  </p>
                  <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>Select all that apply.</p>
                </div>
                <button
                  style={{
                    display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 20,
                    border: `1.5px solid ${T.border}`, background: "#fff", fontSize: 11, color: T.sub,
                    cursor: "pointer", fontWeight: 500, flexShrink: 0,
                  }}
                >
                  ℹ️ Why we ask
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 14 }}>
                {symptoms.map((s) => (
                  <SymptomChip key={s} label={s} selected={selectedSymptoms.includes(s)} onClick={() => toggleSymptom(s)} />
                ))}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: showTryingConceive ? "1fr 1fr" : "1fr", gap: 12 }}>
              <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: T.heading, marginBottom: 8 }}>
                  How long has this been happening?
                </p>
                <StyledSelect value={duration} onChange={setDuration} options={DURATION_OPTS} placeholder="Select duration" />
              </div>
              <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: T.heading, marginBottom: 8 }}>
                  How severe are your symptoms? <span style={{ fontSize: 10, color: T.muted, fontWeight: 400 }}>ℹ️</span>
                </p>
                <StyledSelect value={severity} onChange={setSeverity} options={SEVERITY_OPTS} placeholder="Select severity" />
              </div>
            </div>
          </div>

          <div style={{ width: 230, flexShrink: 0 }}>
            <RightCard>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, margin: 0 }}>Your journey</p>
              </div>

              <div
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  background: stageMeta.color || T.lavenderBg, borderRadius: 10, padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    width: 44, height: 44, borderRadius: 10, background: T.purpleLight,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0,
                  }}
                >
                  {journeyEmoji}
                </div>
                <p style={{ fontWeight: 700, fontSize: 11, color: T.heading, margin: 0, lineHeight: 1.3 }}>{journeyTitle}</p>
              </div>

              <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0 }}>
                We'll personalise your care plan based on your responses.
              </p>

              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 2 }}>Est. time to complete</p>
                <p style={{ fontSize: 11, color: T.sub, margin: 0 }}>~5 min</p>
              </div>

              {selectedSymptoms.length > 0 && (
                <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                  <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 6 }}>Your responses</p>
                  <p style={{ fontSize: 11, color: T.sub, margin: "0 0 8px" }}>
                    {selectedSymptoms.length} symptom{selectedSymptoms.length > 1 ? "s" : ""} selected
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: "12px" }}>
                    <div className="flex flex-wrap gap-2">
                      {selectedSymptoms.map((item, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1.5 rounded-full"
                          style={{ backgroundColor: "var(--blue)", color: "var(--primary-color)" }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                    {selectedSymptoms.length > 5 && (
                      <div
                        style={{
                          width: 30, height: 30, borderRadius: 8, background: T.purpleTag,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 700, color: T.purple700,
                        }}
                      >
                        +{selectedSymptoms.length - 5}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </RightCard>
          </div>
        </div>

        <FooterBar
          left={<BtnBack onClick={onBack} variant="outline" />}
          center={<FooterNote>Your responses help personalise your care journey</FooterNote>}
          right={
            <BtnContinue
              variant="primary"
              label="Continue"
              disabled={selectedSymptoms.length === 0}
              onClick={() => onNext({ selectedSymptoms, duration, severity, tryingToConceive })}
            />
          }
        />
        {showButton && <ScrollToBottomButton onClick={scrollToBottom} />}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 9 — Safety Check
// ─────────────────────────────────────────────────────────────────────────────
export function SafetyCheckScreen({ onNext, onBack, initialSelected = [], stepIndex, totalSteps, 
   careJourneyContent,selectedRoute }) {
  const [selected, setSelected] = useState(initialSelected);
  const { scrollRef, showButton, scrollToBottom } = useScrollToBottom();
  const toggle = label => {
    if (label === "None of these") { setSelected(["None of these"]); return; }
    setSelected(prev => {
      const without = prev.filter(x => x !== "None of these");
      return without.includes(label) ? without.filter(x => x !== label) : [...without, label];
    });
  };
  // console.log(selectedRoute,"this is the selected route")
  const SAFETY_SYMPTOMS = getSafetySymptoms(selectedRoute,careJourneyContent);
  const hasUrgent = selected.length > 0 && !selected.includes("None of these");
  const URGENCY = hasUrgent
    ? { concern: "Possible urgent symptoms reported", action: "Priority review needed", response: "We may guide you to same-day care", concernColor: T.red, actionColor: T.amber, responseColor: T.purple700 }
    : { concern: "No urgent symptoms reported", action: "Standard care pathway", response: "We'll guide you through personalized care", concernColor: T.green, actionColor: T.green, responseColor: T.purple700 };

  return (
    <>
      <div ref={scrollRef} style={{ height: "84vh", overflow: "scroll" }}>
        {/* <PageWrap> */}
        <div className="scj-header">

          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: T.heading, marginBottom: 6 }}>Before we continue, let's make sure you're safe</h1>
            <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>Please tell us if you are experiencing any urgent symptoms right now.</p>
          </div>
          <StepperProgress current={stepIndex} total={totalSteps} />
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ background: "#fff", borderRadius: 14, padding: 16, border: `1px solid ${T.border}`, marginBottom: 14 }}>
              <p style={{ fontWeight: 700, fontSize: 13, color: T.heading, marginBottom: 4 }}>Are you currently experiencing any of these?</p>
              <p style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>Select all that apply.</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {SAFETY_SYMPTOMS.map(({ label, icon }) => {
                  const isSel = selected.includes(label);
                  const isNone = label === "None of these";
                  const isUrg = isSel && !isNone;
                  return (
                    <button key={label} onClick={() => toggle(label)} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                      border: isSel ? `2px solid ${isUrg ? "#FCA5A5" : isNone ? T.green : T.purpleBorder}` : `1.5px solid ${T.border}`,
                      background: isSel ? isUrg ? "#FEF2F2" : isNone ? "#ECFDF5" : T.purpleLight : "#fff",
                    }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: isSel ? isUrg ? "#FEE2E2" : isNone ? "#D1FAE5" : T.purpleLight : "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{icon}</div>
                      <span style={{ fontSize: 12, fontWeight: isSel ? 600 : 400, color: isSel ? isUrg ? "#991B1B" : isNone ? "#065F46" : T.purple700 : T.body, flex: 1 }}>{label}</span>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: isSel ? "none" : `1.5px solid ${T.border}`, background: isSel ? isUrg ? T.red : isNone ? T.green : T.purple700 : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {isSel && <svg width="10" height="8" viewBox="0 0 12 9" fill="none"><path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, display: "flex", alignItems: "flex-start", gap: 8, background: T.infoBg, borderRadius: 8, padding: "8px 10px", border: `1px solid ${T.infoBorder}` }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ️</span>
                <p style={{ fontSize: 10, color: T.infoText, margin: 0, lineHeight: 1.5 }}>If you are pregnant, postpartum, or have severe symptoms, our care team may recommend urgent evaluation.</p>
              </div>
            </div>

            <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `1px solid ${hasUrgent ? "#FCA5A5" : T.border}` }}>
              <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, marginBottom: 10 }}>Urgency Check</p>
              {[
                { icon: "⚠️", label: "Current concern", value: URGENCY.concern, color: URGENCY.concernColor },
                { icon: "🔶", label: "Suggested action", value: URGENCY.action, color: URGENCY.actionColor },
                { icon: "💜", label: "Care team response", value: URGENCY.response, color: URGENCY.responseColor },
              ].map(row => (
                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>{row.icon}</span>
                    <span style={{ fontSize: 11, color: T.body, fontWeight: 500 }}>{row.label}</span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: row.color, textAlign: "right", maxWidth: 200 }}>{row.value}</span>
                </div>
              ))}
              {hasUrgent && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "flex-start", gap: 8, background: "#FFFBEB", borderRadius: 8, padding: "8px 10px", border: `1px solid #FDE68A` }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <p style={{ fontSize: 10, color: "#92400E", margin: 0, lineHeight: 1.5 }}>This screen does not provide a diagnosis. If symptoms are severe, seek urgent medical care.</p>
                </div>
              )}
            </div>
          </div>

          <div style={{ width: 230, flexShrink: 0 }}>
            <RightCard>
              <div style={{ textAlign: "center", fontSize: 48 }}>🛡️</div>
              <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, textAlign: "center" }}>Your safety comes first</p>
              <p style={{ fontSize: 10, color: T.sub, lineHeight: 1.5, margin: 0, textAlign: "center" }}>This quick check helps us identify if you need immediate attention before proceeding.</p>
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                {[
                  { icon: "🚨", title: "Urgent symptoms", desc: "We may direct you to same-day or emergency care." },
                  { icon: "🩺", title: "Non-urgent", desc: "We'll continue personalizing your care journey." },
                  { icon: "🔒", title: "Secure & Private", desc: "Reviewed only by authorized care team members." },
                ].map(f => (
                  <div key={f.title} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: T.purpleLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{f.icon}</div>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 11, color: T.heading, margin: "0 0 2px" }}>{f.title}</p>
                      <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: "center", fontSize: 10, color: T.muted, paddingTop: 4, borderTop: `1px solid ${T.border}` }}>🔒 Secure · Private · Reviewed by care team</div>
            </RightCard>
          </div>
        </div>

        <FooterBar
          left={<BtnBack variant="outline" onClick={onBack} />}
          center={null}
          right={<BtnContinue variant="primary" label={hasUrgent ? "⚠️ Continue — I Understand" : "Continue"} color={hasUrgent ? T.red : T.purpleBtn} disabled={selected.length === 0} onClick={() => onNext({ urgentSymptoms: selected, hasUrgent })} />}
        />
        {/* </PageWrap> */}
        {showButton && (
          <ScrollToBottomButton onClick={scrollToBottom} />
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 10 — Adaptive Questionnaire
// ─────────────────────────────────────────────────────────────────────────────
export function AdaptiveQuestionnaireScreen({ onNext, onBack, allData = {}, totalSteps, stepIndex, careJourneyContent}) {
  const { scrollRef, showButton, scrollToBottom } = useScrollToBottom();
  const questions = buildAdaptiveQuestions(allData,careJourneyContent);
  // const [answers, setAnswers] = useState({});
  const [answers, setAnswers] = useState(allData?.adaptiveAnswers || {});
  const [page, setPage] = useState(0);

  // ── Dynamically resolve life stage ──
  const { stages, stageMeta, stageName, stageDetail } =
    resolveLifeStageFromProfile(allData?.profile || {}, allData?.lifeStageAnswers || {},careJourneyContent);

  const PER_PAGE = 4;
  const totalPages = Math.ceil(questions.length / PER_PAGE);
  const pageQs = questions.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const setAns = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));
  const toggleAns = (id, val) => setAnswers(prev => {
    const cur = prev[id] || [];
    return { ...prev, [id]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
  });

  const pageAnswered = pageQs.every(q => {
    const a = answers[q.id];
    return q.type === "multi" ? a && a.length > 0 : !!a;
  });

  const answered = questions.filter(q => !!answers[q.id] && (q.type !== "multi" || answers[q.id]?.length > 0)).length;
  const progressPct = Math.round((answered / questions.length) * 100);

  const handleNext = () => {
    if (page < totalPages - 1) setPage(p => p + 1);
    else onNext(answers);
  };

  return (
    <>
      {/* <PageWrap> */}
      {/* Dynamic stage tag */}
      <div ref={scrollRef} style={{ height: "84vh", overflow: "scroll" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: stageMeta.color || T.purpleTag, borderRadius: 20, padding: "4px 12px", marginBottom: 14, fontSize: 11, fontWeight: 600, color: T.purple700 }}>
          {stageMeta.emoji} Adaptive Questionnaire · {stageName}
        </div>

        <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ maxWidth: 560 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: T.heading, marginBottom: 6 }}>A few more personalised questions</h1>
            <p style={{ fontSize: 12, color: T.sub, margin: 0 }}>Based on your responses, we've tailored these questions to build the most accurate care plan for you.</p>
          </div>
          <StepperProgress current={stepIndex} total={totalSteps} />
          {/* <div style={{ fontSize: 56, userSelect: "none", marginTop: -8 }}>🎯</div> */}
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            {pageQs.map((question, idx) => {
              const val = answers[question.id];
              const globalIdx = page * PER_PAGE + idx;
              return (
                <div key={question.id} style={{ background: "#fff", borderRadius: 14, padding: 18, border: `1px solid ${val ? T.purpleBorder : T.border}`, transition: "border-color 0.2s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: val ? T.purple700 : T.purpleLight, color: val ? "#fff" : T.purple700, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, transition: "all 0.2s" }}>
                      {val ? "✓" : globalIdx + 1}
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: T.heading, margin: 0, lineHeight: 1.3 }}>{question.q}</p>
                    {question.type === "multi" && <span style={{ fontSize: 10, color: T.muted, background: "#F3F4F6", borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>Multi-select</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {question.options.map(opt => {
                      const active = question.type === "multi" ? (val || []).includes(opt) : val === opt;
                      return (
                        <button key={opt} onClick={() => question.type === "multi" ? toggleAns(question.id, opt) : setAns(question.id, opt)} style={{
                          padding: "8px 16px", borderRadius: 8, fontSize: 12, cursor: "pointer", transition: "all 0.15s",
                          border: active ? `2px solid ${T.purple700}` : `1.5px solid ${T.border}`,
                          background: active ? T.purpleLight : "#fff",
                          color: active ? T.purple700 : T.sub,
                          fontWeight: active ? 600 : 400,
                        }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sidebar */}
          <div style={{ width: 220, flexShrink: 0 }}>
            <RightCard>
              <p style={{ fontWeight: 700, fontSize: 12, color: T.heading, marginBottom: 2 }}>Your progress</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "8px 0" }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", background: T.purpleLight, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: `4px solid ${T.purple700}` }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: T.purple700 }}>{progressPct}%</span>
                  <span style={{ fontSize: 9, color: T.muted }}>complete</span>
                </div>
              </div>
              {/* Questions tracker */}
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                <p style={{ fontWeight: 600, fontSize: 10, color: T.heading, marginBottom: 6 }}>Questions answered</p>
                {questions.map((q, i) => {
                  const ans = !!answers[q.id] && (q.type !== "multi" || answers[q.id]?.length > 0);
                  return (
                    <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: ans ? T.green : T.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, flexShrink: 0, transition: "background 0.3s" }}>
                        {ans && <svg width="8" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      </div>
                      <p style={{ fontSize: 10, color: ans ? T.heading : T.muted, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: ans ? 600 : 400 }}>
                        Q{i + 1}: {q.q.slice(0, 28)}…
                      </p>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: T.lavenderBg, borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "flex-start", gap: 6 }}>
                <span style={{ fontSize: 14 }}>🎯</span>
                <p style={{ fontSize: 10, color: T.sub, margin: 0, lineHeight: 1.4 }}>These questions were selected based on your life stage, symptoms and journey type.</p>
              </div>
            </RightCard>
          </div>
        </div>

        <FooterBar
          left={<BtnBack variant="outline" onClick={page === 0 ? onBack : () => setPage(p => p - 1)} />}
          center={<p style={{ fontSize: 10, color: T.muted, margin: 0 }}>Page {page + 1} of {totalPages}</p>}
          right={<BtnContinue variant="primary" label={page < totalPages - 1 ? "Next" : "Complete Questionnaire"} disabled={!pageAnswered} onClick={handleNext} />}
        />
        {showButton && (
          <ScrollToBottomButton onClick={scrollToBottom} />
        )}

      </div>
      {/* </PageWrap> */}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP PROGRESS NAV
// ─────────────────────────────────────────────────────────────────────────────
const SCREEN_ORDER = [
  "start", "consent", "patient", "profile", "heightweight",
  "lifestage", "entry", "condition", "safety", "adaptive", "done",
];
const SCREEN_LABELS = {
  start: "Journey", consent: "Consent", patient: "Patient",
  profile: "Profile", heightweight: "Height/Wt", lifestage: "Life Stage",
  entry: "Concerns", condition: "Symptoms", safety: "Safety",
  adaptive: "Questions", done: "Done",
};


// ─────────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────────
export default function CareJourney() {
  const [screen, setScreen] = useState("start");
  const [allData, setAllData] = useState({
    selectedJourney: null,
    selectedRoute: null,
    profile: {},
    lifeStageAnswers: {},
  });
  const [careJourneyContent, setCareJourneyContent] = useState({});
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/femcare/care-journey/?clinic=femcare`)
        const data = await res.json();
        setCareJourneyContent(data);
      } catch (error) {
        console.log(error, "error while fetching the json of care journey");
      }
    }
    fetchContent();
  }, [])

  const [patientState, setPatientState] = useState({
    name: '',
    mobile: '',
    dob: '',
    ageGroup: '',
    occupation: '',
    marital: '',
    heightIn: '',
    weightIn: '',
    otp: ['', '', '', '', '', ''],
    otpSent: false,
    otpVerified: false,
    nameError: '',
    mobileError: '',
  });
console.log(allData,"allDataallDataallData")
  const mergePatientState = (d) => setPatientState(prev => ({ ...prev, ...d }));

  const stepIndex = SCREEN_ORDER.indexOf(screen); // 0-based
  const totalSteps = SCREEN_ORDER.length;

  const merge = d => setAllData(prev => ({ ...prev, ...d }));
  const go = key => setScreen(key);

  const SCREENS = {
    start: (
      <StartCareJourneyScreen
        careJourneyContent={careJourneyContent}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        initialSelected={allData.selectedJourney}
        onNext={selectedJourney => { merge({ selectedJourney }); go("consent"); }}
        onBack={() => console.log("Back from start")}
      />
    ),
    consent: (
      <ConsentScreen

        config={careJourneyContent.screens?.consent}

        stepIndex={stepIndex}
        totalSteps={totalSteps}
        onNext={() => go("patient")}
        onBack={() => go("start")}
      />
    ),
    patient: (
      <NewPatientScreen
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        patientState={patientState}
        onPatientStateChange={mergePatientState}
        onNext={() => go("profile")}
        onBack={() => go("consent")}
      />
    ),
    profile: (
      <ProfileScreen 
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        patientState={patientState}
        onPatientStateChange={mergePatientState}
        initialValues={allData.profile}

        onNext={d => {
          merge({ profile: { ...allData.profile, ...d } });

          // ← persist to localStorage
          try {
            const existing = JSON.parse(localStorage.getItem("patientPersona") || "{}");
            localStorage.setItem("patientPersona", JSON.stringify({
              ...existing,
              ...d,               // ageGroup, marital, occupation
              savedAt: Date.now(),
            }));
          } catch (e) {
            console.error("Failed to persist profile to localStorage", e);
          }

          go("heightweight");
        }}
        onBack={() => go("patient")}
      />
    ),
    heightweight: (
      <HeightWeightScreen
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        patientState={patientState}
        onPatientStateChange={mergePatientState}
        initialValues={{ heightIn: allData.heightIn, weightIn: allData.weightIn }}
        onNext={d => { merge(d); go("lifestage"); }}
        onBack={() => go("profile")}
      />
    ),
    lifestage: (
      <BasicLifeStageScreen
      careJourneyContent={careJourneyContent}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        profile={allData.profile}
        initialValues={allData.lifeStageAnswers}
        onNext={d => { merge({ lifeStageAnswers: d }); go("entry"); }}
        onBack={() => go("heightweight")}
      />
    ),
    entry: (
      <EntryReasonScreen
       careJourneyContent={careJourneyContent}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        profile={allData.profile}
        lifeStageAnswers={allData.lifeStageAnswers}
        initialSelected={allData.selectedReasons || []}
        onNext={d => { merge({ selectedReasons: d.selectedReasons, selectedRoute: d.selectedRoute }); go("condition"); }}
        onBack={() => go("lifestage")}
      />
    ),
    condition: (
      <ConditionSpecificIntakeScreen
       careJourneyContent={careJourneyContent}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        selectedRoute={allData.selectedRoute}
        selectedJourney={allData.selectedJourney}
        profile={allData.profile}
        lifeStageAnswers={allData.lifeStageAnswers}   // ← pass through
        initialValues={{
          selectedSymptoms: allData.selectedSymptoms || [],
          duration: allData.duration || "",
          severity: allData.severity || "",
          tryingToConceive: allData.tryingToConceive || "",
        }}
        onNext={d => { merge(d); go("safety"); }}
        onBack={() => go("entry")}
      />
    ),
    safety: (
      <SafetyCheckScreen
       careJourneyContent={careJourneyContent}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        selectedRoute={allData.selectedRoute}
        initialSelected={allData.urgentSymptoms || []}
        onNext={d => { merge(d); go("adaptive"); }}
        onBack={() => go("condition")}
      />
    ),
    adaptive: (
      <AdaptiveQuestionnaireScreen
        stepIndex={stepIndex}
         careJourneyContent={careJourneyContent}
        totalSteps={totalSteps}
        allData={allData}
        onNext={d => { merge({ adaptiveAnswers: d }); go("done"); }}
        onBack={() => go("safety")}
      />
    ),
    done: (
      <CareRecommendationScreen
       careJourneyContent={careJourneyContent}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        allData={{
          ...allData,
          lastPeriod: allData.lifeStageAnswers?.lmp || allData.lastPeriod || "",
        }}
        onBack={() => go("adaptive")}
      />
    ),
  };

  return (
    <>
      <DigitalHealthFAB />
      <div className={styles.page}>
        <div className={styles.card}>
          <ScreenHeaderDFO />
          <div style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>
            {SCREENS[screen]}
          </div>
        </div>
      </div>
    </>
  );
}